namespace $ {

	type Definition = $shm_hitalama_map_widget_entity_custom
	type Source = $shm_hitalama_map_component_compiler_source

	/** Map-owned definitions; runtime scopes never publish user classes to window. */
	export class $shm_hitalama_map_component extends $mol_object {

		map() { return null as any as $shm_hitalama_map }

		@ $mol_mem
		compiler(): $shm_hitalama_map_component_compiler { return new this.$.$shm_hitalama_map_component_compiler }

		@ $mol_mem
		preparation(): $shm_hitalama_map_component_preparation {
			return this.$.$shm_hitalama_map_component_preparation.make({ $: this.$ })
		}

		@ $mol_mem
		hints() {
			return this.$.$shm_hitalama_map_component_preparation_hints.make({ $: this.$ })
		}

		definition( fqn: string ) {
			// Legacy map FQNs (including definitions shared from another map)
			// remain usable without waiting for an unrelated domain catalogue.
			if( !/^\$[A-Za-z0-9Ææ]{8}_[A-Za-z0-9Ææ]{8}_/.test( fqn ) ) {
				const shared = this.$.$shm_hitalama_components_catalog.current()?.definition( fqn )
				if( shared ) return shared
			}
			const map = this.map()
			if( !map ) throw new Error( `Компонент ${ fqn } не найден в каталоге домена.` )
			this.$.$shm_hitalama_map_component_ready.read( map )
			const catalogue = map.Customs()
			if( catalogue ) this.$.$shm_hitalama_map_component_ready.read( catalogue )
			const matches = ( catalogue?.remote_list() ?? [] ).filter( item => {
				this.$.$shm_hitalama_map_component_ready.read( item )
				return item.class_name() === fqn
			} )
			if( matches.length !== 1 ) throw new Error( matches.length
				? `Несколько определений ${ fqn }. Задайте уникальные имена компонентов.`
				: `Компонент ${ fqn } не найден в каталоге карты.` )
			return matches[0]
		}

		@ $mol_mem_key
		graph( root: Definition ) {
			this.preparation().read( root )
			const ordered: { definition: Definition, source: Source }[] = []
			const visited = new Map< string, string >
			const stack: string[] = []
			const visit = ( definition: Definition )=> {
				this.$.$shm_hitalama_map_component_ready.read( definition )
				const name = definition.class_name()
				const id = definition.link().str
				if( stack.includes( id ) ) throw new Error( `Циклическая зависимость: ${ [ ...stack, id ].join( ' → ' ) }` )
				if( visited.has( name ) ) {
					if( visited.get( name ) !== id ) throw new Error( `Конфликт определений ${ name }` )
					return
				}
				if( !/^\$[A-Za-z0-9_]+$/.test( name ) ) throw new Error( `Недопустимый FQN компонента: ${ name }` )
				if( definition.source_format() === 'ts' ) {
					const catalog = definition.Owner_catalog()?.remote()
					const owner = definition.Owner_map()?.remote()
					if( catalog ) {
						if( catalog.definition( name )?.link().str !== id ) throw new Error( `Компонент ${ name } не зарегистрирован в своём каталоге.` )
					} else if( !owner || !name.startsWith( '$' + owner.link().str + '_' ) ) {
						throw new Error( `FQN должен начинаться с $${ owner?.link().str ?? this.map().link().str }_` )
					}
				}
				stack.push( id )
				for( const dependency of definition.dependencies() ) {
					const target = this.$.$giper_baza_glob.Pawn( new this.$.$giper_baza_link( dependency.link ), this.$.$shm_hitalama_map_widget_entity_custom )
					this.$.$shm_hitalama_map_component_ready.read( target )
					if( target.class_name() !== dependency.fqn ) throw new Error( `Зависимость ${ dependency.fqn } не соответствует определению ${ dependency.link }` )
					visit( target )
				}
				stack.pop()
				visited.set( name, id )
				for( const kind of [ 'tree', 'logic', 'style' ] as const ) {
					const source = definition.source_link( kind )
					if( source ) this.$.$shm_hitalama_map_component_ready.read( source )
				}
				ordered.push({ definition, source: {
					fqn: name, tree: definition.source_text( 'tree' ), logic: definition.source_text( 'logic' ),
					style: definition.source_text( 'style' ), format: definition.source_format(),
				} })
			}
			visit( root )
			return ordered
		}

		@ $mol_mem_key
		component_class( fqn: string ): any {
			this.hints().warm( fqn )
			const root = this.definition( fqn )
			const graph = this.graph( root )
			// Read/compile the entire graph before executing any user code.
			const artifacts = this.compiler().compiled_many( graph.map( item => this.compiler().signature( item.source ) ) )
			const scope = this.scope( JSON.stringify( graph.map( ( item, index )=> ({ source: item.source, output: artifacts[index] }) ) ) )
			if( !Object.hasOwn( scope, fqn ) ) throw new Error( `Модуль ${ fqn } не определяет экспорт ${ fqn }.` )
			this.hints().remember( fqn, graph )
			// Saving a changed manifest invalidates warm(): do not return with stale
			// hint dependencies. Executable classes still come only from this graph.
			this.hints().warm( fqn )
			return scope[ fqn ]
		}

		@ $mol_mem_key
		scope( key: string ): Record< string, any > {
			const graph = JSON.parse( key ) as { source: Source, output: $shm_hitalama_map_component_compiler_output }[]
			const scope = Object.create( this.$ )
			scope.$ = scope
			scope.$$ = scope
			scope[ '$' + 'map' ] = this.map()
			const declared = new Set( graph.map( item => item.source.fqn ) )
			for( const { output } of graph ) for( const name of output.references ) {
				if( !declared.has( name ) && !( name in scope ) ) throw new Error( `Компонент ${ name } недоступен. Добавьте его в явные зависимости.` )
			}
			const locales = Object.assign( {}, ...graph.map( item => item.output.locale ) )
			const ambientLocale = this.$.$mol_locale
			scope.$mol_locale = class extends ambientLocale {
				static text( name: string ) { return locales[ name ] ?? super.text( name ) }
			}
			for( const { source, output } of graph ) {
				const previous = new Map( Object.entries( scope ) )
				this.evaluate( output.tree, output.bindings.tree, scope, source.fqn + '.view.tree.js' )
				this.evaluate( output.logic, output.bindings.logic, scope, source.fqn + '.view.ts.js' )
				// Bind all newly declared constructors, including model/helper classes.
				// Functions and data exports do not need to be views (or constructors).
				for( const [ name, value ] of Object.entries( scope ) ) {
					if( previous.get( name ) === value || typeof value !== 'function' || !value.prototype ) continue
					// Exporting an alias must never rebind a framework class globally.
					if( this.$[ value.name as keyof typeof this.$ ] === value ) continue
					if( Object.isExtensible( value.prototype ) && !Object.hasOwn( value.prototype, '$' ) ) Object.defineProperty( value.prototype, '$', { get: ()=> scope, configurable: true } )
					if( Object.isExtensible( value ) && !Object.hasOwn( value, '$' ) ) Object.defineProperty( value, '$', { get: ()=> scope, configurable: true } )
				}
				if( source.format === 'ts' ) this.evaluate( output.style, output.bindings.style, scope, source.fqn + '.view.css.ts.js' )
				else this.$.$mol_style_attach( source.fqn, source.style )
			}
			return scope
		}

		evaluate( code: string, bindings: string[], scope: Record< string, any >, uri: string ) {
			// Bare FQN helpers/decorators work as in a repository module. No global publication.
			const names = bindings.filter( name => name in scope )
			const aliases = names.map( name => `const ${ name } = $[${ JSON.stringify( name ) }];` ).join( '\n' )
			new Function( '$', '"use strict";\n' + aliases + '\n' + code + '\n//# sourceURL=hitalama://component/' + uri )( scope )
		}

	}

}
